/**
 * Lighting rig — directional sun + shadows + IBL + night floodlights. (§6.2)
 *
 * This subsystem owns everything about how the scene is LIT: the key light and
 * its real-time shadow, the image-based ambient/reflection environment, and the
 * night floodlight fills. Post-processing (tone mapping) is deliberately NOT
 * here — postfx.ts owns the renderer's tone mapping, and these two coordinate
 * only through `ctx.scene` (environment/background) and that split (see the
 * brief). Everything below is subordinate to the 60fps budget (§6.8): the Low
 * tier (Intel iGPU) gets NO real-time shadows and NO floodlight shadows at all.
 *
 * ── Why a single tight shadow cam instead of three.js CSM (§6.2 "state your
 *    choice") ────────────────────────────────────────────────────────────────
 * The brief asks for cascaded shadow maps but explicitly allows "a single tight
 * shadow-cam that follows the camera focus as a pragmatic stand-in." We take
 * that path on purpose, not for lack of a CSM: three's `CSM` requires calling
 * `csm.setupMaterial(material)` on EVERY material that should receive cascades,
 * injecting an `onBeforeCompile` hook into it. Those materials are created by
 * OTHER subsystems (pitch/players/stadium), in their own files, AFTER this rig
 * initialises — reaching into them would break the one-file-per-agent seam the
 * whole render layer is built on (§15.2). A standard three DirectionalLight
 * shadow, by contrast, is sampled automatically by any material whose mesh sets
 * `receiveShadow` — zero cross-subsystem coupling. We keep the shadow frustum
 * TIGHT and slide it to follow the broadcast camera's focus each frame, which
 * puts the resolution where the play is ("sharp near shadows"); far shadows
 * simply fall outside the frustum, an accepted trade for a localized-action
 * broadcast view.
 *
 * ── Grounding (light probes, §6.2) ──────────────────────────────────────────
 * True per-object light probes are overkill at this camera distance; a
 * HemisphereLight (sky tint above, pitch-green bounce below) is the cheap
 * stand-in that grounds players in ambient, layered on top of the PMREM IBL.
 */

import {
  AdditiveBlending,
  CanvasTexture,
  DirectionalLight,
  DoubleSide,
  EquirectangularReflectionMapping,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PCFSoftShadowMap,
  PlaneGeometry,
  PMREMGenerator,
  PointLight,
  Quaternion,
  SpotLight,
  SRGBColorSpace,
  Vector3,
  type Texture,
  type WebGLRenderTarget,
} from 'three';
import { BALL_RADIUS, MAX_PLAYERS, type MatchConfig } from '@fg/shared/sim';
import {
  QUALITY_RANK,
  type QualitySettings,
  type RenderContext,
  type RenderState,
  type RenderSubsystem,
} from './context.js';

/**
 * The sky/weather presets. Default is Day Clear (D0). The original four
 * (day/golden/overcast/night) are §6.2's brief and stay byte-for-byte
 * reachable exactly as before through `setSky`/`applyTimeOfDay` — nothing
 * about them changed. `cloudy`, `rain` and `heavyRain` are the three ADDED
 * for the stadium-presentation ask (owner: "rainy, heavy rain, cloudy,
 * overcast"): distinct sky/light/fog looks, not just the wet-pitch albedo
 * change `config.wetPitch` already drove. `night` gained real floodlight
 * shadows and dugout lights (see FLOOD_SHADOW_COUNT / dugout lights below);
 * `rain`/`heavyRain` turn the floods on too — broadcast convention (and
 * genuine visibility) is floodlights burning through rain regardless of the
 * clock.
 *
 * ⚠ REACHABILITY: `ui/settings.ts`'s `TimeOfDay` union (day/golden/overcast/
 * night) and its setup-screen dropdown are UNCHANGED — that file and
 * `ui/menu.ts` are outside this task's scope, so a player cannot pick
 * `cloudy`/`rain`/`heavyRain` from the pre-match screen yet. They ARE fully
 * real, live, tested presets on the actual match renderer, selectable today
 * via `ui/environment.ts`'s `applySkyPreset` (which this module's `setSky`
 * backs) — see that file's header. Wiring three dropdown rows + two type
 * unions in `ui/settings.ts`/`ui/menu.ts` is the one remaining step to put
 * them in front of a player, and is intentionally left to whoever owns those
 * files.
 */
export type SkyPreset = 'day' | 'golden' | 'cloudy' | 'overcast' | 'night' | 'rain' | 'heavyRain';

/**
 * The lighting rig, exposed as a superset of RenderSubsystem so the orchestrator
 * can drive the time-of-day (§6.2 "expose a way to pick sky/time"). Assignable
 * to RenderSubsystem, so subsystems.ts wires it in unchanged.
 */
export interface LightingRig extends RenderSubsystem {
  /** Pick the sky/time-of-day. Rebuilds IBL lazily; safe to call any time. */
  setSky(preset: SkyPreset, ctx: RenderContext): void;
  currentSky(): SkyPreset;
}

interface SkyParams {
  /** Unit direction FROM the pitch TOWARD the sun (light travels the other way). */
  sunDir: Vector3;
  sunColor: number;
  sunIntensity: number;
  /** Real-time shadow strength 0..1 — soft/low under overcast, full in sun. */
  shadowIntensity: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  /** Global multiplier on the IBL contribution (scene.environmentIntensity). */
  envIntensity: number;
  /** Zenith / horizon / lower colours for the procedural equirect sky. */
  zenith: string;
  horizon: string;
  ground: string;
  /** Whether floodlight fills (and the dugout lights) are lit — night, and
   *  now also rain/heavyRain (floods stay on through rain, broadcast §6.2). */
  floodsOn: boolean;
  /** Ground fog for the murkier weather looks. Undefined = no fog (day/
   *  golden/night keep a clear sky; night's darkness does that job itself). */
  fog?: { color: string; near: number; far: number };
}

// The physically-loose but visually-tuned presets. Numbers are art, not sim, so
// they live here rather than constants.ts (that file is the SIM's tunables).
/**
 * Total direct irradiance a flat, up-facing, fully-lit surface receives under a
 * preset — the sun's contribution plus the hemisphere fill.
 *
 * Exported because CEL-SHADED materials have to be normalised against it.
 * `MeshToonMaterial` quantises N·L through a ramp but still multiplies by the
 * FULL irradiance, so a `sunIntensity` tuned to put a PBR pitch near the top of
 * the ACES shoulder pushes a toon surface straight past its ramp and flattens
 * every band into white. Reading the number here rather than hard-coding it
 * means retuning a preset re-normalises the cel look automatically instead of
 * silently washing it out. See `stadium-v5/toonLook.ts`.
 */
export function directIrradianceFor(preset: SkyPreset): number {
  const p = SKY_PRESETS[preset];
  return p.sunIntensity + p.hemiIntensity;
}

const SKY_PRESETS: Record<SkyPreset, SkyParams> = {
  day: {
    // Elevation ~31°, matching the anime-stadium export's key light (its README
    // specifies "one directional light, warm, low (~31 deg elevation) + a cool
    // hemisphere fill"). The old 55° sat the sun almost overhead, which flattens
    // the bowl's relief and shortens the shadows the cel look reads against.
    // Same azimuth as before; only the height changed.
    sunDir: new Vector3(0.677, 0.515, 0.523).normalize(),
    sunColor: 0xfff4e2,
    // 4.2/0.9 (was 3.1/0.55): tuned against live captures — through the ACES
    // stack the old values read as dusk; broadcast daytime needs the pitch
    // near the top of the tonemap's shoulder.
    sunIntensity: 4.2,
    shadowIntensity: 1.0,
    hemiSky: 0x9ec8ff,
    hemiGround: 0x3a6b34,
    hemiIntensity: 0.9,
    envIntensity: 1.0,
    zenith: '#2a6bd8',
    horizon: '#bfe0f5',
    ground: '#20361f',
    floodsOn: false,
    // The anime bowl's aerial haze (Blender FA_BowlFog, density 0.0016 —
    // volumes don't export to glTF, and its README says to reproduce the base
    // haze with scene fog IN ADDITION TO the crowd's per-height tint). Linear
    // ramp calibrated to its measured profile: ~5% contrast loss at 35 m, ~12%
    // at 80, ~19-20% at 135, so play distances stay clean while far stands
    // recede. Colour is the export's own #a8c2eb, matched by the crowd shader's
    // uHazeColor — mismatch there shows as a seam where crowd meets structure.
    fog: { color: '#a8c2eb', near: 10, far: 620 },
  },
  golden: {
    // Low sun → long, warm shadows across the pitch (§6.2 golden hour).
    sunDir: new Vector3(0.9, 0.24, 0.36).normalize(),
    sunColor: 0xffd39a,
    sunIntensity: 2.6,
    shadowIntensity: 0.95,
    hemiSky: 0xffb27a,
    hemiGround: 0x4a3a2a,
    hemiIntensity: 0.5,
    envIntensity: 0.95,
    zenith: '#3b4a8a',
    horizon: '#ff9d5c',
    ground: '#2a2016',
    floodsOn: false,
  },
  cloudy: {
    // "Partly cloudy" — brighter and more contrasty than overcast (a visible
    // sun disc smudges through broken cloud, softer shadow than day's crisp
    // one), but plainly not sunny. Sits BETWEEN day and overcast on every
    // axis so the three read as a real progression, not three flat greys.
    sunDir: new Vector3(0.4, 0.78, 0.32).normalize(),
    sunColor: 0xf3ecdf,
    sunIntensity: 2.6,
    shadowIntensity: 0.62,
    hemiSky: 0xaec2d6,
    hemiGround: 0x3d5a37,
    hemiIntensity: 1.05,
    envIntensity: 1.05,
    zenith: '#4a7bc2',
    horizon: '#cfdcea',
    ground: '#24361f',
    floodsOn: false,
  },
  overcast: {
    // Diffuse key, soft low-contrast shadow, high ambient — a flat grey sky.
    sunDir: new Vector3(0.25, 0.9, 0.15).normalize(),
    sunColor: 0xdfe6ee,
    sunIntensity: 1.4,
    shadowIntensity: 0.35,
    hemiSky: 0xb9c2cc,
    hemiGround: 0x45514a,
    hemiIntensity: 0.9,
    envIntensity: 1.15,
    zenith: '#95a2 b0'.replace(' ', ''),
    horizon: '#c3ccd4',
    ground: '#2f3a33',
    floodsOn: false,
  },
  night: {
    // A dim cool moon key remains the single shadow-caster; floodlights do the
    // real lifting as additive UNSHADOWED fills (§6.2 "one shadow-casting light
    // + additive unshadowed fills").
    sunDir: new Vector3(-0.3, 0.72, -0.25).normalize(),
    sunColor: 0x9fb4d8,
    sunIntensity: 0.5,
    shadowIntensity: 0.85,
    hemiSky: 0x1a2440,
    hemiGround: 0x0e1a12,
    hemiIntensity: 0.35,
    envIntensity: 0.6,
    zenith: '#05070f',
    horizon: '#0e1626',
    ground: '#050a07',
    floodsOn: true,
  },
  rain: {
    // A dark, wet-slate DAY sky — this is "match is on despite the weather",
    // not dusk. Sun all-diffuse (no disc, no shadow to speak of — real rain
    // sky is one giant softbox), floods burning already because visibility
    // under a rain sky is poor enough that broadcasts light through it.
    sunDir: new Vector3(0.2, 0.92, 0.1).normalize(),
    sunColor: 0xc3ccd6,
    sunIntensity: 0.9,
    shadowIntensity: 0.2,
    hemiSky: 0x707c8a,
    hemiGround: 0x323b34,
    hemiIntensity: 0.85,
    envIntensity: 0.85,
    zenith: '#414d5c',
    horizon: '#7c8894',
    ground: '#232c26',
    floodsOn: true,
    fog: { color: '#616d7a', near: 40, far: 210 },
  },
  heavyRain: {
    // Heavier than `rain` on every axis that reads as "worse", not just
    // darker uniformly — lower sun, cooler/greyer hemisphere, denser and
    // NEARER fog (the far stands should start dissolving), floods doing more
    // of the work. This is the pairing the owner's "rainy, HEAVY rain" ask
    // needs to actually look like two different things side by side.
    sunDir: new Vector3(0.15, 0.88, 0.08).normalize(),
    sunColor: 0x9aa4b0,
    sunIntensity: 0.5,
    shadowIntensity: 0.1,
    hemiSky: 0x525d6a,
    hemiGround: 0x262e29,
    hemiIntensity: 0.7,
    envIntensity: 0.62,
    zenith: '#2c343f',
    horizon: '#565f6a',
    ground: '#181f1b',
    floodsOn: true,
    fog: { color: '#454d56', near: 22, far: 145 },
  },
};

/** Shadow-cam follow distance along the sun ray. */
const SHADOW_LIGHT_DISTANCE = 140;
/** Ortho half-extent of the shadow frustum per tier — tighter = sharper. (§6.2)
 *
 *  High was a 100 m box at 2048px, i.e. 4.9 cm per texel — paying for a full
 *  extra scene pass and spending it on a resolution that cannot resolve a boot.
 *  The frustum already follows the camera focus (see updateShadowFocus below),
 *  so a smaller box loses nothing the broadcast camera is actually looking at
 *  and roughly doubles the effective shadow resolution at the same cost. */
const SHADOW_HALF_EXTENT: Record<QualitySettings['tier'], number> = {
  low: 0,
  medium: 32,
  high: 32,
  ultra: 45,
};
const FLOOD_COUNT = 6;
const FLOOD_HEIGHT = 26;
const DEFAULT_HALF_L = 105 / 2;
const DEFAULT_HALF_W = 68 / 2;

/**
 * Real floodlights read as floodlights partly BECAUSE a player standing under
 * them casts more than one shadow — the owner's explicit ask ("give the
 * multi-shadow look real floodlights produce"). The rig's other 4 towers stay
 * additive-unshadowed exactly as before (§6.2's own reasoning: six full shadow
 * passes at night is not a 60fps night match) — this lights the two towers on
 * a DIAGONAL (opposite corners) so the two shadows a player casts fall at a
 * clearly different angle from each other, which is what sells "multiple
 * lights", not merely "two lights are on". High/Ultra only (§6.8): a shadow
 * pass costs real ms per light and Low/Medium already run zero/blob shadows.
 */
const FLOOD_SHADOW_COUNT = 2;
const FLOOD_SHADOW_MAP_SIZE: Record<QualitySettings['tier'], number> = {
  low: 0,
  medium: 0,
  high: 768,
  ultra: 1024,
};

/**
 * Two small warm fixtures under the dugout roofs (owner: "lights near
 * dugout"). Coordinates MIRROR stadium.ts's dugout geometry (DUGOUT_CX=8,
 * DUGOUT_FRONT_OFF=1.3, DUGOUT_H=2.2 — see stadium.ts's own constants of the
 * same name) rather than importing it: stadium.ts is off-limits to this task
 * (sideline.ts hits the same wall and mirrors for the same reason — see its
 * header). If stadium.ts ever moves a dugout, update this mirror or the
 * lights float free of the shelter they're meant to be lighting.
 */
const DUGOUT_CX = 8;
const DUGOUT_FRONT_OFF = 1.3;
const DUGOUT_H = 2.2;
const DUGOUT_LIGHT_COLOR = 0xfff0d0;
const DUGOUT_LIGHT_INTENSITY = 55;
const DUGOUT_LIGHT_RANGE = 9;

class LightingSystem implements LightingRig {
  readonly name = 'lighting';

  private sky: SkyPreset = 'day';
  private readonly sun = new DirectionalLight(0xffffff, 1);
  private readonly sunTarget = new Object3D();
  private readonly hemi = new HemisphereLight(0xffffff, 0x444444, 1);
  private readonly floodGroup = new Group();
  private readonly floods: SpotLight[] = [];
  private readonly dugoutGroup = new Group();
  private readonly dugoutLights: PointLight[] = [];

  /** Cached procedural IBL per preset, generated lazily on first use. */
  private readonly bgTex: Partial<Record<SkyPreset, Texture>> = {};
  private readonly envRT: Partial<Record<SkyPreset, WebGLRenderTarget>> = {};
  private pmrem: PMREMGenerator | null = null;

  /** Cheap blob shadows for the Low tier (no real-time shadow map). */
  private blob: InstancedMesh | null = null;
  private blobTex: CanvasTexture | null = null;
  private shadowsEnabled = false;

  private halfL = DEFAULT_HALF_L;
  private halfW = DEFAULT_HALF_W;

  // ── Preallocated scratch — update() is the 60Hz path, allocates nothing (§15.7).
  private readonly _dir = new Vector3();
  private readonly _focus = new Vector3();
  private readonly _blobPos = new Vector3();
  private readonly _blobScale = new Vector3(1, 1, 1);
  private readonly _blobQuat = new Quaternion(); // identity; plane pre-rotated flat
  private readonly _mtx = new Matrix4();

  init(ctx: RenderContext): void {
    const gl = ctx.gl;
    // The lighting rig owns the renderer's shadow config (as postfx owns tone
    // mapping). Leave shadowMap.enabled ON always and gate via light.castShadow
    // + mapSize: three recompiles materials automatically when the shadow-light
    // count changes, so a tier switch costs one hitch, not a manual audit.
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = PCFSoftShadowMap;

    this.sun.castShadow = true;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.02;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = SHADOW_LIGHT_DISTANCE * 2;
    ctx.scene.add(this.sun);
    ctx.scene.add(this.sunTarget);
    this.sun.target = this.sunTarget;

    ctx.scene.add(this.hemi);
    ctx.scene.add(this.floodGroup);
    ctx.scene.add(this.dugoutGroup);

    this.buildBlobShadows(ctx);
    this.buildFloods();
    this.buildDugoutLights();
    this.configureFloodShadows(ctx.quality);

    this.pmrem = new PMREMGenerator(gl);
    this.pmrem.compileEquirectangularShader();

    this.applyPreset(this.sky, ctx);
    this.configureShadow(ctx.quality);
  }

  onPitchConfig(config: MatchConfig, _ctx: RenderContext): void {
    this.halfL = config.pitchLength * 0.5;
    this.halfW = config.pitchWidth * 0.5;
    // Floodlight towers + dugout lights hug the (new) stadium footprint.
    this.positionFloods();
    this.positionDugoutLights();
  }

  update(state: RenderState, ctx: RenderContext): void {
    if (this.shadowsEnabled) {
      this.followShadow(ctx);
    } else if (this.blob) {
      this.updateBlobShadows(state);
    }
  }

  setQuality(quality: QualitySettings, ctx: RenderContext): void {
    this.configureShadow(quality);
    this.configureFloodShadows(quality);
    // Flood count depends on tier at night; re-apply so it takes effect live.
    if (SKY_PRESETS[this.sky].floodsOn) this.applyFloods(ctx.quality);
  }

  setSky(preset: SkyPreset, ctx: RenderContext): void {
    this.sky = preset;
    this.applyPreset(preset, ctx);
  }

  currentSky(): SkyPreset {
    return this.sky;
  }

  dispose(): void {
    for (const p of Object.values(this.envRT)) p?.dispose();
    for (const t of Object.values(this.bgTex)) t?.dispose();
    this.pmrem?.dispose();
    this.sun.shadow.map?.dispose();
    this.blob?.geometry.dispose();
    (this.blob?.material as MeshBasicMaterial | undefined)?.dispose();
    this.blobTex?.dispose();
    for (const s of this.floods) s.shadow.map?.dispose();
  }

  // ── IBL + light state ──────────────────────────────────────────────────────

  private applyPreset(preset: SkyPreset, ctx: RenderContext): void {
    const p = SKY_PRESETS[preset];
    this.ensureSky(preset, ctx);

    ctx.scene.background = this.bgTex[preset] ?? null;
    ctx.scene.environment = this.envRT[preset]?.texture ?? null;
    // environmentIntensity globally scales the IBL without touching any material
    // (three r163+), so the pitch/players/stadium pick it up for free.
    ctx.scene.environmentIntensity = p.envIntensity;

    this.sun.color.setHex(p.sunColor);
    this.sun.intensity = p.sunIntensity;
    this.sun.shadow.intensity = p.shadowIntensity;

    this.hemi.color.setHex(p.hemiSky);
    this.hemi.groundColor.setHex(p.hemiGround);
    this.hemi.intensity = p.hemiIntensity;

    // Ground fog for the murky weather looks (rain/heavyRain) — lighting.ts
    // already owns scene.background/environment, so scene.fog sits with them
    // rather than crossing into postfx.ts's territory. Undefined = clear, and
    // three simply stops fogging (scene.fog = null) rather than leaving a
    // stale one on a preset that never asked for it.
    ctx.scene.fog = p.fog ? new Fog(p.fog.color, p.fog.near, p.fog.far) : null;

    this.applyFloods(ctx.quality);
  }

  /** Build the procedural equirect sky + its PMREM env once per preset. (§16 —
   *  no downloaded HDRIs; everything is synthesised on a canvas.) */
  private ensureSky(preset: SkyPreset, ctx: RenderContext): void {
    if (this.bgTex[preset]) return;
    const p = SKY_PRESETS[preset];

    const canvas = drawSkyCanvas(preset, p);
    const bg = new CanvasTexture(canvas);
    bg.mapping = EquirectangularReflectionMapping;
    bg.colorSpace = SRGBColorSpace;
    bg.needsUpdate = true;
    this.bgTex[preset] = bg;

    if (this.pmrem) {
      // Prefilter the same equirect into a mip-chained radiance env. One-time
      // cost at init/first-pick; never touched per frame.
      this.envRT[preset] = this.pmrem.fromEquirectangular(bg);
    }
    void ctx;
  }

  // ── Shadow ──────────────────────────────────────────────────────────────────

  private configureShadow(q: QualitySettings): void {
    if (q.shadowMapSize > 0) {
      this.shadowsEnabled = true;
      this.sun.castShadow = true;
      // Realloc the shadow map at the new resolution.
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
      this.sun.shadow.mapSize.set(q.shadowMapSize, q.shadowMapSize);
      const half = SHADOW_HALF_EXTENT[q.tier] || 48;
      const cam = this.sun.shadow.camera;
      cam.left = -half;
      cam.right = half;
      cam.top = half;
      cam.bottom = -half;
      cam.updateProjectionMatrix();
      if (this.blob) this.blob.visible = false;
    } else {
      // Low tier (Intel iGPU, §6.8): no shadow pass at all — blob decals ground
      // the players instead.
      this.shadowsEnabled = false;
      this.sun.castShadow = false;
      if (this.blob) this.blob.visible = true;
    }
  }

  /** Slide the shadow frustum to sit under the broadcast camera's focus so the
   *  limited-resolution map is spent where the action is. (§6.2) */
  private followShadow(ctx: RenderContext): void {
    const cam = ctx.camera;
    cam.getWorldDirection(this._dir);
    let fx: number;
    let fz: number;
    if (Math.abs(this._dir.y) > 1e-4) {
      // Intersect the view ray with the pitch plane y=0.
      const t = -cam.position.y / this._dir.y;
      fx = cam.position.x + this._dir.x * t;
      fz = cam.position.z + this._dir.z * t;
    } else {
      fx = cam.position.x;
      fz = cam.position.z;
    }
    // Keep the focus (and thus the shadowed region) inside the stadium footprint.
    const limX = this.halfL + 10;
    const limZ = this.halfW + 10;
    fx = fx < -limX ? -limX : fx > limX ? limX : fx;
    fz = fz < -limZ ? -limZ : fz > limZ ? limZ : fz;
    this._focus.set(fx, 0, fz);

    const d = SKY_PRESETS[this.sky].sunDir;
    this.sun.position.set(
      fx + d.x * SHADOW_LIGHT_DISTANCE,
      d.y * SHADOW_LIGHT_DISTANCE,
      fz + d.z * SHADOW_LIGHT_DISTANCE,
    );
    this.sunTarget.position.copy(this._focus);
    this.sunTarget.updateMatrixWorld();
  }

  // ── Blob shadows (Low tier) ─────────────────────────────────────────────────

  private buildBlobShadows(ctx: RenderContext): void {
    this.blobTex = new CanvasTexture(drawBlobCanvas());
    this.blobTex.colorSpace = SRGBColorSpace;
    // Pre-rotate the quad flat so per-instance rotation stays identity.
    const geo = new PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2);
    const mat = new MeshBasicMaterial({
      map: this.blobTex,
      transparent: true,
      depthWrite: false,
      color: 0x000000,
      opacity: 0.5,
    });
    // +1 for the ball's own blob.
    this.blob = new InstancedMesh(geo, mat, MAX_PLAYERS + 1);
    this.blob.frustumCulled = false;
    this.blob.renderOrder = 1;
    this.blob.visible = false;
    // Park every instance at zero scale until update() places the live ones.
    this._mtx.makeScale(0, 0, 0);
    for (let i = 0; i < MAX_PLAYERS + 1; i++) this.blob.setMatrixAt(i, this._mtx);
    this.blob.instanceMatrix.needsUpdate = true;
    ctx.scene.add(this.blob);
  }

  private updateBlobShadows(state: RenderState): void {
    const mesh = this.blob;
    if (!mesh) return;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (state.active[i] === 1) {
        this._blobPos.set(state.px[i]!, 0.02, state.pz[i]!);
        this._blobScale.set(1.35, 1, 1.35);
      } else {
        this._blobScale.set(0, 0, 0);
        this._blobPos.set(0, -1000, 0);
      }
      this._mtx.compose(this._blobPos, this._blobQuat, this._blobScale);
      mesh.setMatrixAt(i, this._mtx);
    }
    // Ball blob shrinks as the ball rises (a lofted ball casts a smaller,
    // fainter mark) — cheap fake occlusion for the Low tier.
    const b = state.ball;
    const lift = b.py > BALL_RADIUS ? b.py : BALL_RADIUS;
    const s = Math.max(0.25, (BALL_RADIUS * 3) / lift);
    this._blobPos.set(b.px, 0.02, b.pz);
    this._blobScale.set(s, 1, s);
    this._mtx.compose(this._blobPos, this._blobQuat, this._blobScale);
    mesh.setMatrixAt(MAX_PLAYERS, this._mtx);
    mesh.instanceMatrix.needsUpdate = true;
  }

  // ── Floodlights ─────────────────────────────────────────────────────────────

  private buildFloods(): void {
    for (let i = 0; i < FLOOD_COUNT; i++) {
      const s = new SpotLight(0xfff2d6, 0, 320, 0.62, 0.45, 1.35);
      s.castShadow = false; // additive fills only — multi-shadow kills fps (§6.2)
      s.target = new Object3D();
      this.floodGroup.add(s);
      this.floodGroup.add(s.target);
      this.floods.push(s);
    }
    this.positionFloods();
  }

  private positionFloods(): void {
    // Four corner towers + two halfway-line towers, all aimed at the near third
    // so the pool of light spreads across the whole surface.
    const cx = this.halfL + 8;
    const cz = this.halfW + 6;
    const spots: Array<[number, number, number, number]> = [
      [cx, cz, this.halfL * 0.4, this.halfW * 0.4],
      [-cx, cz, -this.halfL * 0.4, this.halfW * 0.4],
      [cx, -cz, this.halfL * 0.4, -this.halfW * 0.4],
      [-cx, -cz, -this.halfL * 0.4, -this.halfW * 0.4],
      [0, cz + 2, 0, this.halfW * 0.3],
      [0, -cz - 2, 0, -this.halfW * 0.3],
    ];
    for (let i = 0; i < this.floods.length; i++) {
      const s = this.floods[i]!;
      const cfg = spots[i]!;
      s.position.set(cfg[0], FLOOD_HEIGHT, cfg[1]);
      s.target.position.set(cfg[2], 0, cfg[3]);
      s.target.updateMatrixWorld();
      // Indices 0 and 3 are TRUE diagonal opposites in the `spots` table above
      // (+cx,+cz) and (-cx,-cz) — the pair configureFloodShadows arms.
      if (s.shadow.camera) {
        s.shadow.camera.near = 4;
        s.shadow.camera.far = FLOOD_HEIGHT + this.halfL + this.halfW;
        s.shadow.camera.updateProjectionMatrix();
      }
    }
  }

  /** Arm/disarm the two diagonal floods' shadow maps for the tier (§6.8 — a
   *  shadow pass per light is real cost, so Low/Medium get none, matching the
   *  sun's own tier gate). Sized once here, not every applyFloods call, so a
   *  quality switch doesn't thrash allocations on every preset change too. */
  private configureFloodShadows(q: QualitySettings): void {
    const size = FLOOD_SHADOW_MAP_SIZE[q.tier];
    for (let i = 0; i < FLOOD_SHADOW_COUNT && i < this.floods.length; i++) {
      const s = this.floods[i]!;
      if (size > 0) {
        s.shadow.mapSize.set(size, size);
        s.shadow.bias = -0.0015;
        s.shadow.normalBias = 0.02;
        s.shadow.map?.dispose();
        s.shadow.map = null;
      } else {
        s.castShadow = false;
        s.shadow.map?.dispose();
        s.shadow.map = null;
      }
    }
  }

  private applyFloods(q: QualitySettings): void {
    const on = SKY_PRESETS[this.sky].floodsOn;
    // Scale the number of *active* towers with the tier: an Intel iGPU should not
    // shade six spotlights per fragment at night (§6.8). Low leans on a brighter
    // hemisphere instead (handled by the preset's ambient) and lights zero.
    const rank = QUALITY_RANK[q.tier];
    const active = !on ? 0 : rank <= 0 ? 0 : rank === 1 ? 3 : FLOOD_COUNT;
    const shadowSize = FLOOD_SHADOW_MAP_SIZE[q.tier];
    // 620 at REFERENCE_ACTIVE=3 was the measured, not-blown figure (see the
    // wet-pitch note below). It was applied UNSCALED to every tower regardless
    // of how many were lit — so High/Ultra's 6 towers each still burned 620,
    // and their cones overlap in the pitch centre and near each base, so the
    // OVERLAP zones ran at roughly double the measured energy. Rendered proof
    // (a real Ultra-tier practice match, elevated + pitch-level cameras) showed
    // exactly that: a soft white wash bloomed clean across the centre circle —
    // not present at the reference count. Scaling intensity by
    // REFERENCE_ACTIVE/active keeps any given point's total received light
    // roughly constant as the tower count rises (more, dimmer cones cover MORE
    // of the pitch at the SAME peak brightness, which is the actual point of
    // spending the extra towers), while medium/low — already at 3 or 0 active
    // — are numerically unchanged from before this pass.
    const REFERENCE_ACTIVE = 3;
    const FLOOD_BASE_INTENSITY = 950;
    const intensity = active > 0 ? FLOOD_BASE_INTENSITY * (REFERENCE_ACTIVE / active) : 0;
    for (let i = 0; i < this.floods.length; i++) {
      const s = this.floods[i]!;
      const lit = i < active;
      s.visible = lit;
      // 900 clipped hard on a wet pitch (glare wall through bloom); 620 still
      // reads floodlit-bright through ACES. Measured, not vibes — see pitch.ts.
      s.intensity = lit ? intensity : 0;
      // Only the two diagonal towers (see positionFloods) ever cast, and only
      // when they are actually lit AND the tier affords a shadow map.
      s.castShadow = lit && shadowSize > 0 && i < FLOOD_SHADOW_COUNT;
    }
    this.applyDugoutLights(on);
  }

  // ── Dugout lights ───────────────────────────────────────────────────────────

  /** Two small warm fixtures under the dugout roofs (owner: "lights near
   *  dugout") — see the DUGOUT_* constants' header for the stadium.ts mirror. */
  private buildDugoutLights(): void {
    for (const side of [-1, 1] as const) {
      const l = new PointLight(DUGOUT_LIGHT_COLOR, 0, DUGOUT_LIGHT_RANGE, 2);
      l.castShadow = false; // two fixtures more is not worth another shadow pass
      this.dugoutGroup.add(l);
      this.dugoutLights.push(l);
      void side;
    }
    this.positionDugoutLights();
  }

  private positionDugoutLights(): void {
    // Under the shelter roof (DUGOUT_H), just in front of the back wall, one
    // per dugout at x = ±DUGOUT_CX — see stadium.buildDugouts for the shelter
    // this sits inside of.
    const z = -(this.halfW + DUGOUT_FRONT_OFF) - 0.6;
    const y = DUGOUT_H - 0.35;
    for (let i = 0; i < this.dugoutLights.length; i++) {
      const x = i === 0 ? -DUGOUT_CX : DUGOUT_CX;
      this.dugoutLights[i]!.position.set(x, y, z);
    }
  }

  /** Dugout lights track the same on/off signal as the floodlights (night,
   *  rain, heavyRain — see each preset's `floodsOn`): a covered technical area
   *  is lit whenever the ground needs its floods, not on a separate clock. */
  private applyDugoutLights(on: boolean): void {
    for (const l of this.dugoutLights) l.intensity = on ? DUGOUT_LIGHT_INTENSITY : 0;
  }
}

export function createLightingRig(): LightingRig {
  return new LightingSystem();
}

// ─────────────────────────────────────────────────────────────────────────────
// Procedural asset synthesis (§16 — nothing downloaded). All canvas-drawn.
// ─────────────────────────────────────────────────────────────────────────────

/** Deterministic PRNG for cloud/star scatter — sky is drawn once per preset, so
 *  the layout must not shuffle between sessions or PMREM rebuilds. */
function skyRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SKY_SEEDS: Record<SkyPreset, number> = {
  day: 0xa11ce,
  golden: 0xb0b0b,
  cloudy: 0xc10dea,
  overcast: 0xc0ffee,
  night: 0xdecade,
  rain: 0x2a19ed,
  heavyRain: 0x2a19e6,
};

type Rgb = [number, number, number];
function hexRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix3(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
function cssRgb(c: Rgb): string {
  return `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;
}

/**
 * An equirectangular sky: multi-stop vertical gradient, an analytic sun/moon
 * glow placed from the preset's `sunDir`, and soft blurred clouds (day/golden/
 * overcast) or stars (night). Used both as scene.background and as the PMREM
 * source for IBL. Drawn ONCE per preset at first pick — never per frame — so
 * canvas allocation here is fine (§15.7 applies to update(), not this).
 *
 * The gradient stops interpolate ONLY the preset's existing zenith/horizon/
 * ground colours, and the glow/cloud alphas stay close to the old single-glow
 * energy, so the PMREM/IBL level the light intensities were just tuned against
 * barely moves (the instruction is: richer sky, same lighting).
 */
function drawSkyCanvas(preset: SkyPreset, p: SkyParams): HTMLCanvasElement {
  const W = 1024;
  const H = 512;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // ── Vertical gradient. Equirect V runs top=zenith → bottom=ground. Extra
  // stops give the day sky a rich deep zenith that brightens into the horizon
  // instead of the old flat two-band wash.
  const zen = hexRgb(p.zenith);
  const hor = hexRgb(p.horizon);
  const gnd = hexRgb(p.ground);
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0.0, p.zenith);
  grad.addColorStop(0.2, cssRgb(mix3(zen, hor, 0.3)));
  grad.addColorStop(0.38, cssRgb(mix3(zen, hor, 0.68)));
  grad.addColorStop(0.47, p.horizon);
  grad.addColorStop(0.53, p.horizon);
  grad.addColorStop(0.62, cssRgb(mix3(hor, gnd, 0.6)));
  grad.addColorStop(1.0, p.ground);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Sun/moon equirect position FROM the preset's sunDir (u=azimuth,
  // v=elevation), so the painted glow always agrees with where the rig
  // actually points the key light and where shadows fall.
  const d = p.sunDir;
  const az = Math.atan2(d.z, d.x); // -π..π
  const el = Math.asin(Math.max(-1, Math.min(1, d.y))); // -π/2..π/2
  const su = (az / (Math.PI * 2) + 0.5) * W;
  const sv = (0.5 - el / Math.PI) * H;
  // Everything positional is drawn at u and u±W so the equirect wrap is seamless.
  const wraps: readonly number[] = [-W, 0, W];

  const glow = (u: number, v: number, r: number, stops: ReadonlyArray<readonly [number, string]>): void => {
    for (const off of wraps) {
      const g = ctx.createRadialGradient(u + off, v, 0, u + off, v, r);
      for (const s of stops) g.addColorStop(s[0], s[1]);
      ctx.fillStyle = g;
      ctx.fillRect(u + off - r, v - r, r * 2, r * 2);
    }
  };

  // ── Sun / moon disc + halo per preset.
  if (preset === 'day') {
    // Bright warm core + wide soft halo — the daylight anchor of the sky.
    glow(su, sv, 170, [
      [0, 'rgba(255,250,238,0.9)'],
      [0.12, 'rgba(255,242,210,0.55)'],
      [1, 'rgba(255,242,210,0)'],
    ]);
    glow(su, sv, 34, [
      [0, 'rgba(255,255,250,1)'],
      [0.55, 'rgba(255,250,235,0.9)'],
      [1, 'rgba(255,250,235,0)'],
    ]);
  } else if (preset === 'golden') {
    // Low fat orange sun with a long warm halo hugging the horizon.
    glow(su, sv, 240, [
      [0, 'rgba(255,196,128,0.85)'],
      [0.15, 'rgba(255,160,90,0.5)'],
      [1, 'rgba(255,150,80,0)'],
    ]);
    glow(su, sv, 40, [
      [0, 'rgba(255,236,200,1)'],
      [0.5, 'rgba(255,206,140,0.9)'],
      [1, 'rgba(255,190,120,0)'],
    ]);
  } else if (preset === 'cloudy') {
    // A visible but soft sun disc through broken cloud — brighter/tighter
    // than overcast's flat smudge, nowhere near day's hard core.
    glow(su, sv, 210, [
      [0, 'rgba(255,255,248,0.5)'],
      [0.3, 'rgba(255,250,235,0.24)'],
      [1, 'rgba(255,250,235,0)'],
    ]);
    glow(su, sv, 46, [
      [0, 'rgba(255,255,252,0.85)'],
      [0.6, 'rgba(255,250,240,0.5)'],
      [1, 'rgba(255,250,240,0)'],
    ]);
  } else if (preset === 'overcast') {
    // No disc — just the broad faint bright smudge a diffuse sun leaves.
    glow(su, sv, 260, [
      [0, 'rgba(255,255,255,0.22)'],
      [0.5, 'rgba(255,255,255,0.1)'],
      [1, 'rgba(255,255,255,0)'],
    ]);
  } else if (preset === 'night') {
    // Night: a small cool moon + faint halo (the preset's dim key light).
    glow(su, sv, 90, [
      [0, 'rgba(214,228,255,0.5)'],
      [0.2, 'rgba(190,210,250,0.18)'],
      [1, 'rgba(190,210,250,0)'],
    ]);
    glow(su, sv, 11, [
      [0, 'rgba(240,246,255,0.95)'],
      [0.7, 'rgba(226,236,255,0.8)'],
      [1, 'rgba(226,236,255,0)'],
    ]);
  } else if (preset === 'rain') {
    // A thin, cold smudge — you can tell roughly where the sun is, nothing
    // more. Noticeably dimmer than overcast's already-faint one.
    glow(su, sv, 220, [
      [0, 'rgba(220,226,232,0.14)'],
      [0.5, 'rgba(220,226,232,0.06)'],
      [1, 'rgba(220,226,232,0)'],
    ]);
  } else {
    // heavyRain: no discernible sun at all — the whole point is that you
    // cannot tell where it is. No glow() call.
  }

  // ── Clouds (day/golden/cloudy/overcast/rain/heavyRain) or stars (night).
  const rng = skyRng(SKY_SEEDS[preset]);
  if (preset === 'night') {
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 160; i++) {
      const u = rng() * W;
      const v = rng() * H * 0.46; // above the horizon only
      const s = 0.6 + rng() * 1.3;
      ctx.globalAlpha = 0.2 + rng() * 0.7;
      ctx.fillRect(u, v, s, s);
    }
    ctx.globalAlpha = 1;
  } else {
    // A cloud = a cluster of soft radial puffs, squashed into an ellipse.
    const puff = (u: number, v: number, r: number, squash: number, rgb: string, alpha: number): void => {
      for (const off of wraps) {
        ctx.save();
        ctx.translate(u + off, v);
        ctx.scale(1, squash);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
        g.addColorStop(0, `rgba(${rgb},${alpha})`);
        g.addColorStop(0.65, `rgba(${rgb},${alpha * 0.5})`);
        g.addColorStop(1, `rgba(${rgb},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.restore();
      }
    };
    // Cover grows from a handful of day cumulus to a total rain-deck: day 6 <
    // golden 4(warm, sparse) < cloudy 9 (broken, still gaps of blue) <
    // overcast 12 (flat, unbroken) < rain 16 < heavyRain 20 (dense, low, dark).
    const clusters =
      preset === 'heavyRain' ? 20
        : preset === 'rain' ? 16
        : preset === 'overcast' ? 12
        : preset === 'cloudy' ? 9
        : preset === 'day' ? 6 : 4;
    for (let i = 0; i < clusters; i++) {
      const cu = rng() * W;
      // day: mid-sky cumulus; golden/overcast/rain family: sit lower, toward
      // the horizon (a rain deck reads as a low, heavy ceiling).
      const vMin =
        preset === 'day' ? 0.16
          : preset === 'golden' ? 0.28
          : preset === 'cloudy' ? 0.12
          : preset === 'rain' || preset === 'heavyRain' ? 0.02 : 0.06;
      const vSpan = preset === 'overcast' || preset === 'rain' || preset === 'heavyRain' ? 0.38 : 0.16;
      const cv = (vMin + rng() * vSpan) * H;
      const baseR =
        preset === 'overcast' || preset === 'rain' || preset === 'heavyRain' ? 90 + rng() * 70 : 40 + rng() * 50;
      // Overcast/rain/heavyRain alternate lighter/darker grey blobs into a
      // mottled deck (heavyRain's pair is darker still); day is white
      // cumulus; golden is warm-lit; cloudy is bright near-white with a
      // little grey mixed in (broken cloud, not a solid deck).
      const rgb =
        preset === 'day'
          ? '255,255,255'
          : preset === 'golden'
            ? '255,214,170'
            : preset === 'cloudy'
              ? i % 3 === 0 ? '214,220,226' : '250,250,248'
              : preset === 'heavyRain'
                ? i % 2 === 0 ? '150,158,166' : '90,98,106'
                : preset === 'rain'
                  ? i % 2 === 0 ? '188,196,204' : '124,134,144'
                  : i % 2 === 0
                    ? '208,216,224'
                    : '134,146,158';
      const alpha =
        preset === 'overcast' ? 0.25
          : preset === 'day' ? 0.5
          : preset === 'cloudy' ? 0.4
          : preset === 'rain' ? 0.32
          : preset === 'heavyRain' ? 0.4 : 0.35;
      const n = 2 + Math.floor(rng() * 3);
      for (let k = 0; k < n; k++) {
        puff(
          cu + (rng() - 0.5) * baseR * 1.6,
          cv + (rng() - 0.5) * baseR * 0.5,
          baseR * (0.55 + rng() * 0.6),
          0.42,
          rgb,
          alpha,
        );
      }
    }
  }
  return canvas;
}

/** A soft round drop-shadow decal for the Low-tier blob shadows. */
function drawBlobCanvas(): HTMLCanvasElement {
  const S = 128;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.75)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  return canvas;
}

// Silence unused-import pruning for symbols kept for readability of intent.
void AdditiveBlending;
void DoubleSide;
void Mesh;
